import { useState } from "react";
import { useOutletContext, useParams } from "react-router-dom";
import Chip from "../../../components/Chip";
import EntityPicker from "../../../components/EntityPicker";
import { useAuth } from "../../../../auth/useAuth";
import { useMembersList } from "../../../../hooks/useMembersList";
import {
  useAssignParcoursMember,
  useParcoursMembers,
  useUnassignParcoursMember,
} from "../../../../hooks/useParcoursMembers";
import type { ParcoursResponse } from "../../../../api/parcoursApi";

export default function ParcoursMembersTab() {
  const { id = "" } = useParams<{ id: string }>();
  const parcours = useOutletContext<ParcoursResponse>();
  const { isAdmin } = useAuth();
  const [search, setSearch] = useState("");

  const assignedQ = useParcoursMembers(id);
  const membersQ = useMembersList();
  const assign = useAssignParcoursMember(id);
  const unassign = useUnassignParcoursMember(id);

  const assigned = assignedQ.data ?? [];
  const assignedIds = assigned.map((m) => m.id);

  // Only EDITOR members are candidates — admins already see every parcours.
  const q = search.trim().toLowerCase();
  const candidates = (membersQ.data ?? [])
    .filter((m) => m.role === "EDITOR" && m.status === "ACTIVE")
    .filter((m) => {
      if (!q) return true;
      const label = (m.userName ?? m.userId ?? "").toLowerCase();
      return label.includes(q);
    })
    .map((m) => ({
      id: m.id,
      label: m.userName ?? m.userId ?? "—",
      sublabel: m.userId,
    }));

  if (!isAdmin) {
    return (
      <div className="bo-card" style={{ textAlign: "center" }}>
        <p style={{ color: "var(--ink-mute)", fontSize: 14, margin: 0 }}>
          La gestion des membres assignés est réservée aux administrateurs.
        </p>
      </div>
    );
  }

  return (
    <div className="bo-stack">
      <div className="bo-card">
        <h3 style={{ margin: 0, marginBottom: "var(--sp-4)", fontFamily: "var(--font-display)", fontSize: 18 }}>
          Membres assignés ({assigned.length})
        </h3>
        <p style={{ color: "var(--ink-mute)", fontSize: 12, marginTop: 0 }}>
          Les éditeurs listés ci-dessous peuvent modifier ce parcours.
          Les administrateurs y ont accès automatiquement.
        </p>
        {assigned.length === 0 ? (
          <p style={{ color: "var(--ink-mute)", fontSize: 13, margin: 0 }}>
            Aucun éditeur assigné à « {parcours.title} ».
          </p>
        ) : (
          <div className="bo-chip-list">
            {assigned.map((m) => (
              <Chip
                key={m.id}
                label={m.userName ?? m.userId ?? "—"}
                onRemove={() => unassign.mutate(m.id)}
              />
            ))}
          </div>
        )}
      </div>

      <div className="bo-card">
        <h3 style={{ margin: 0, marginBottom: "var(--sp-3)", fontFamily: "var(--font-display)", fontSize: 18 }}>
          Assigner un éditeur
        </h3>
        <EntityPicker
          placeholder="Rechercher un membre par nom…"
          options={candidates}
          excludeIds={assignedIds}
          onSearchChange={setSearch}
          onPick={(o) => assign.mutate(o.id)}
          loading={membersQ.isLoading}
        />
        {membersQ.data && membersQ.data.filter((m) => m.role === "EDITOR").length === 0 && (
          <p style={{ color: "var(--ink-mute)", fontSize: 12, marginTop: "var(--sp-3)" }}>
            Aucun éditeur dans ce tenant. Invitez-en depuis <a href="/admin/members/invite">Membres → Inviter</a>.
          </p>
        )}
      </div>
    </div>
  );
}
